import clsx from "clsx";
import React, { DetailedHTMLProps, HTMLAttributes } from "react";
import { useFormContext } from "react-hook-form";

import { GenericApiValue } from "../../../../api-http/types";
import { FormFieldStatus, Input, Select } from "../../elements";
import { AddressFormFieldsLabels, AddressFormFieldsTypes } from "./types";

export interface AddressFormFieldsProps
  extends Pick<DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement>, "className"> {
  labels: AddressFormFieldsLabels;
  countriesList: GenericApiValue[] | null;
  statesList: GenericApiValue[] | null;
}

export function AddressFormFields({
  labels,
  countriesList: countryList,
  statesList: stateList,
  className,
}: AddressFormFieldsProps) {
  const formMethods = useFormContext<AddressFormFieldsTypes>();
  const { errors } = formMethods.formState;

  const selectedCountry = formMethods.watch("country");

  return (
    <div className={clsx("grid gap-4 grid-cols-12 w-full", className)}>
      <Select
        className="col-span-12 lg:col-span-6"
        label={labels.country}
        message={errors.country?.message ?? undefined}
        status={errors.country?.message ? FormFieldStatus.Error : FormFieldStatus.Warning}
        required
        optionalLabel={labels.optionalLabel}
        {...formMethods?.register("country", {
          required: labels.requiredFieldMessage,
          onChange: () => {
            formMethods.setValue("stateId", null);
            formMethods.setValue("state", null);
          },
        })}
      >
        <option value="" disabled>
          {labels.selectCountryPlaceholder}
        </option>
        {countryList?.map(({ id, value }) => (
          <option key={id} value={id}>
            {value}
          </option>
        ))}
      </Select>

      <Input
        className="col-span-6 lg:col-span-2"
        label={labels.zipCode}
        required={false}
        {...formMethods?.register("zipCode")}
        message={errors.zipCode?.message}
        status={FormFieldStatus.Error}
      />

      {!(selectedCountry === "IT") || !stateList?.length ? (
        <Input
          className="col-span-6 lg:col-span-4"
          label={labels.state}
          message={errors.state?.message}
          status={FormFieldStatus.Error}
          {...formMethods?.register("state", {
            onChange: () => {
              formMethods.setValue("stateId", null);
            },
          })}
        />
      ) : (
        <Select
          className="col-span-6 lg:col-span-4"
          label={labels.state}
          message={errors.state?.message ?? undefined}
          status={errors.state?.message ? FormFieldStatus.Error : FormFieldStatus.Warning}
          {...formMethods?.register("stateId", {
            onChange: () => {
              formMethods.setValue("state", null);
            },
          })}
        >
          <option value="" disabled>
            {labels.selectCountryPlaceholder}
          </option>
          {stateList?.map(({ id, value }) => (
            <option key={id} value={id}>
              {value}
            </option>
          ))}
        </Select>
      )}
      <Input
        className="col-span-12 lg:col-span-4"
        label={labels.city}
        {...formMethods?.register("city")}
        message={errors.state?.message}
        status={FormFieldStatus.Error}
      />
    </div>
  );
}
